import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import moment from 'moment';

import { useAppSelector } from '../../../../hooks';
import { classCancellationReasonOptions } from '../../../../constants';
import {
  cancelCoursZoomeSectionAsUser,
  getSectionByIdAsUser,
  getSectionsState,
  resetUserSection,
  ISection,
  TCancelCourseZoomSectionAsUser,
  SECTION_TYPES,
  TCancelCourseTeamsSectionAsUser,
  cancelCourseTeamsSectionAsUser,
} from '../../../../store/V2/SectionSlice';
import {
  IDENTIFIED_USER_ROLES,
  TIdentifiedUserRole,
} from '../../../../store/UserSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import BasicLoader from '../../../../components/Loaders/BasicLoader';

type ClassCancellationModalProps = {
  open: boolean;
  sectionId: string;
  identifiedUserRole: TIdentifiedUserRole;
  sectionType: ISection['type'];
  onClose: () => void;
  onSubmitted: (section: ISection) => void;
};

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedReason: TSelectOption | null;
  otherReason: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const alertStyles: React.CSSProperties = {
  borderRadius: '4px',
  backgroundColor: '#F1F5F9',
  color: '#1E293B',
  padding: '10px 14px',
  textAlign: 'center',
};

const actionButtonStyles: SxProps = {
  borderRadius: '5px',
  padding: { xs: '8px 7px', sm: '16px 14px' },
  fontWeight: 600,
  fontSize: { xs: '12px', sm: '14px' },
  maxHeight: '48px',
};

const initialState: TState = {
  selectedReason: null,
  otherReason: '',
};

const ClassCancellationModal: React.FC<ClassCancellationModalProps> = ({
  open,
  sectionId,
  identifiedUserRole,
  sectionType,
  onClose,
  onSubmitted,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const sectionsState = useAppSelector(getSectionsState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const sectionData = sectionsState.userSection;

  let cancellationWarningMessage = '';

  if (sectionData && sectionData.dateTime) {
    const toClassStartHours = Math.floor(
      moment.duration(moment(sectionData.dateTime).diff(moment())).asHours()
    );

    if (identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR) {
      if (toClassStartHours < 12) {
        cancellationWarningMessage =
          'A penalty of 200% of the class fee will be applied for cancelling under 12 hours';
      } else if (toClassStartHours < 24) {
        cancellationWarningMessage =
          'A penalty of 100% of the class fee will be applied for cancelling under 24 hours';
      }
    } else if (identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT) {
      if (toClassStartHours < 2) {
        cancellationWarningMessage =
          'A penalty of 100% of the class fee will be applied for cancelling under 2 hours';
      }
    }
  }

  useEffect(() => {
    dispatch(getSectionByIdAsUser({ id: sectionId }));

    return () => {
      dispatch(resetUserSection());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (state.selectedReason) {
      setLoading(true);

      if (sectionType === SECTION_TYPES.ZOOM) {
        const apiData: TCancelCourseZoomSectionAsUser = {
          _id: sectionId,
          reason: state.selectedReason.value,
        };

        if (state.selectedReason.value === 'Other') {
          apiData.reason = state.otherReason.trim();
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res: any = await dispatch(cancelCoursZoomeSectionAsUser(apiData));

        if (res.payload) {
          onSubmitted(res.payload);
        }
      } else if (sectionType === SECTION_TYPES.TEAMS) {
        const apiData: TCancelCourseTeamsSectionAsUser = {
          _id: sectionId,
          reason: state.selectedReason.value,
        };

        if (state.selectedReason.value === 'Other') {
          apiData.reason = state.otherReason.trim();
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res: any = await dispatch(
          cancelCourseTeamsSectionAsUser(apiData)
        );

        if (res.payload) {
          onSubmitted(res.payload);
        }
      }

      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: isXsScreen ? '15px' : '30px 30px 20px 30px',
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Cancel Class
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: isXsScreen ? '7px' : '22px',
          top: isXsScreen ? '7px' : '22px',
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: isXsScreen ? '0 15px' : '0 30px' }}>
        <div role="presentation">
          {sectionsState.status === 'loading' && !sectionData ? (
            <Stack sx={{ p: '32px' }}>
              <BasicLoader />
            </Stack>
          ) : (
            <Stack gap="10px">
              {identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR ? (
                <>
                  <div style={alertStyles}>
                    <Typography fontWeight={500} fontSize="12px">
                      Please note that, all tutor&apos;s requests for class
                      cancellation require admin approval. If approved, student
                      will be notified.
                    </Typography>
                  </div>
                  <div
                    style={{
                      ...alertStyles,
                      backgroundColor: '#fdebeb',
                      color: '#ff2b2d',
                    }}
                  >
                    <Typography fontWeight={500} fontSize="12px">
                      In the event your request is not accepted by the student,
                      a cancellation penalty will be applied if the class does
                      not take place.
                    </Typography>
                  </div>
                </>
              ) : null}
              {identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT ? (
                <>
                  <div style={alertStyles}>
                    <Typography fontWeight={500} fontSize="12px">
                      Please note that, all student&apos;s requests for class
                      cancellation require admin approval. If approved, tutor
                      will be notified.
                    </Typography>
                  </div>
                </>
              ) : null}
              {cancellationWarningMessage ? (
                <div
                  style={{
                    ...alertStyles,
                    backgroundColor: '#fdebeb',
                    color: '#ff2b2d',
                  }}
                >
                  <Typography fontWeight={500} fontSize="12px">
                    {cancellationWarningMessage}
                  </Typography>
                </div>
              ) : null}
              <div style={{ marginTop: '10px' }}>
                <Typography color="#475569" fontSize="14px" fontWeight={400}>
                  Are you sure? Do you want to cancel the class?
                </Typography>
              </div>
              <div>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={state.selectedReason}
                  options={[
                    ...classCancellationReasonOptions,
                    { label: 'Other', value: 'Other' },
                  ]}
                  onChange={(e, value) =>
                    updateStateAttributes({ selectedReason: value })
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Reason for cancelation *"
                      placeholder="Select"
                    />
                  )}
                />
              </div>
              {state.selectedReason &&
              state.selectedReason.value === 'Other' ? (
                <Collapse in>
                  <div>
                    <TPTextField
                      {...textFieldProps}
                      fullWidth
                      size="small"
                      label="Other Reason"
                      placeholder="Enter here..."
                      value={state.otherReason}
                      onChange={(e) =>
                        updateStateAttributes({ otherReason: e.target.value })
                      }
                    />
                  </div>
                </Collapse>
              ) : null}
            </Stack>
          )}
        </div>
      </DialogContent>
      {sectionData ? (
        <DialogActions sx={{ p: isXsScreen ? '15px' : '30px' }}>
          <LoadingButton
            disableElevation
            loading={isLoading}
            onClick={handleSubmit}
            disabled={
              !state.selectedReason ||
              (state.selectedReason &&
                state.selectedReason.value === 'Other' &&
                !state.otherReason.trim().length)
            }
            sx={{
              ...actionButtonStyles,
              color: '#FF5630',
              backgroundColor: 'rgba(255, 86, 48, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 86, 48, 0.1)',
              },
            }}
          >
            Cancel Class
          </LoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default ClassCancellationModal;
